import React, { createContext, type ReactNode, useContext, useState } from 'react';

import { calcDatasourcePollutantInfo, DatasourcePollutantInfo, findAqiLevel } from '../shared/aqi';
import {
  AirQualityMarkerDto,
  AqiLevelDto,
  AqiStandardId,
  AqiStandardV2Dto,
  MapSettingsDto,
  MetricIndex,
  MetricInfoDto,
  MetricKey,
  NetworkId,
  NetworkV2Dto,
  Pollutant,
  PollutantConfigDto,
} from '../types';

export class PollutantInfo {
  pollutant: Pollutant;
  displayName: string;
  aqiInfo: MetricInfoDto;
  concInfo: MetricInfoDto;
}

interface SettingsContextType {
  marker: AirQualityMarkerDto | null;
  setMarker: (marker: AirQualityMarkerDto | null) => void;

  mapSettings: MapSettingsDto | null;
  setMapSettings: (mapSettings: MapSettingsDto | null) => void;

  network: NetworkV2Dto | null;
  networkId: NetworkId | null;
  aqiStandard: AqiStandardV2Dto | null;
  availableAqiStandards: AqiStandardV2Dto[] | null;
  pollutantConfigs: PollutantConfigDto[] | null;
  setNetworkInfo: (
    network: NetworkV2Dto,
    aqiStandards: AqiStandardV2Dto[],
    aqiStandardOverride?: AqiStandardV2Dto
  ) => void;
  selectNetworkId: (networkId: NetworkId) => void;
  selectAqiStandardId: (aqiStandardId: AqiStandardId) => void;

  // calculated values:
  datasourcePollutantInfos: DatasourcePollutantInfo[] | null;
  aqiLevel: AqiLevelDto | null;
  aqiMetricKeys: MetricKey[] | null;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [marker, setMarker] = useState<AirQualityMarkerDto | null>(null);
  const [network, setNetwork] = useState<NetworkV2Dto | null>(null);
  const [aqiStandard, setAqiStandard] = useState<AqiStandardV2Dto | null>(null);
  const [availableAqiStandards, setAvailableAqiStandards] = useState<AqiStandardV2Dto[] | null>(null);
  const [networkId, setNetworkId] = useState<NetworkId | null>(null);
  const [aqiStandardId, setAqiStandardId] = useState<AqiStandardId | null>(null);
  const [mapSettings, setMapSettings] = useState<MapSettingsDto | null>(null);

  const setNetworkInfo = (
    network: NetworkV2Dto,
    aqiStandards: AqiStandardV2Dto[],
    aqiStandardOverride?: AqiStandardV2Dto
  ) => {
    // When changing the network, we need to reset some state.
    setNetwork(network);
    setNetworkId(network.networkId);
    setAvailableAqiStandards(aqiStandards);

    const aqiStandard = aqiStandardOverride ?? aqiStandards[0];
    setAqiStandard(aqiStandard); // set the first AQI standard as the default
    setAqiStandardId(aqiStandard.aqiStandardId);
  };

  // This function immediately resets the necessary state
  // when the network changes while setNetworkInfo might
  // take some time to complete. This is useful to allow for
  // UI updates to happen immediately.
  const selectNetworkId = React.useCallback(
    (newNetworkId: NetworkId) => {
      if (newNetworkId !== networkId) {
        setMarker(null);
        setNetwork(null);
        setAqiStandard(null);
      }
      setNetworkId(newNetworkId);
    },
    [networkId]
  );

  const selectAqiStandardId = React.useCallback(
    (newAqiStandardId: AqiStandardId) => {
      if (newAqiStandardId !== aqiStandardId) {
        const newAqiStandard = availableAqiStandards?.find((s) => s.aqiStandardId === newAqiStandardId) ?? null;
        setAqiStandard(newAqiStandard);
        setAqiStandardId(newAqiStandardId);
      }
    },
    [aqiStandardId, availableAqiStandards]
  );

  const pollutantConfigs = React.useMemo(() => {
    if (!network || !aqiStandard) {
      return null;
    }
    return aqiStandard.pollutantConfigs.filter(({ pollutant }) => network.enabledPollutants.includes(pollutant));
  }, [network, aqiStandard]);

  const datasourcePollutantInfos = React.useMemo(() => {
    if (!marker || !pollutantConfigs || !aqiStandard) {
      return null;
    }
    return calcDatasourcePollutantInfo(marker.metrics, pollutantConfigs, aqiStandard);
  }, [marker, pollutantConfigs, aqiStandard]);

  const aqiLevel = React.useMemo(() => {
    if (!aqiStandard) {
      return null;
    }
    return findAqiLevel(aqiStandard, datasourcePollutantInfos?.[0].aqi);
  }, [aqiStandard, datasourcePollutantInfos]);

  const pollutantInfos = React.useMemo(() => {
    if (!pollutantConfigs) {
      return null;
    }
    return pollutantConfigs.map(({ pollutant, displayName, metricInfos }) => ({
      pollutant,
      displayName,
      aqiInfo: metricInfos.find((info) => info.indexType == MetricIndex.Aqi)!,
      concInfo: metricInfos.find((info) => info.indexType == MetricIndex.Unindexed)!,
    }));
  }, [pollutantConfigs]);

  const aqiMetricKeys = React.useMemo(() => {
    if (!pollutantInfos) {
      return null;
    }
    return pollutantInfos.map((p) => p.aqiInfo.key).flat();
  }, [pollutantInfos]);

  return (
    <SettingsContext.Provider
      value={{
        marker,
        setMarker,
        network,
        aqiStandard,
        availableAqiStandards,
        pollutantConfigs,
        setNetworkInfo,
        datasourcePollutantInfos,
        aqiLevel,
        selectNetworkId,
        selectAqiStandardId,
        networkId,
        aqiMetricKeys,
        mapSettings,
        setMapSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
